'use client';

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save, Loader2 } from 'lucide-react';
import type { SurveyRecord } from '@/types/survey';
import type { SurveyStatus } from './StatusBadge';

interface EditRecordModalProps {
  isOpen: boolean;
  record: SurveyRecord | null;
  onSave: (updated: SurveyRecord) => void;
  onClose: () => void;
  isSaving: boolean;
}

interface EditFormValues {
  pid: string;
  username: string;
  status: SurveyStatus;
  ip: string;
}

const statusOptions: { value: SurveyStatus; label: string }[] = [
  { value: 'complete', label: 'Complete' },
  { value: 'terminate', label: 'Terminate' },
  { value: 'qualitySecurity', label: 'Quality / Security' },
  { value: 'quotaFull', label: 'Quota Full' },
];

export default function EditRecordModal({
  isOpen,
  record,
  onSave,
  onClose,
  isSaving,
}: EditRecordModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditFormValues>();

  useEffect(() => {
    if (record) {
      reset({
        pid: record.pid,
        username: record.username,
        status: record.status,
        ip: record.ip,
      });
    }
  }, [record, reset]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen || !record) return null;

  const onSubmit = (data: EditFormValues) => {
    onSave({ ...record, ...data });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-modal-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" onClick={onClose} />
      <div className="relative glass-card rounded-xl border border-border w-full max-w-lg shadow-2xl slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h3 id="edit-modal-title" className="text-base font-semibold text-foreground">
              Edit Survey Record
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">Record ID: {record.id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            aria-label="Close edit modal"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4">
          {/* PID */}
          <div>
            <label htmlFor="edit-pid" className="block text-sm font-medium text-foreground mb-1">
              Project ID (PID)
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">
              The survey project identifier from the redirect URL
            </p>
            <input
              id="edit-pid"
              {...register('pid', { required: 'PID is required' })}
              className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
              placeholder="e.g. PRJ-2847"
            />
            {errors.pid && (
              <p className="mt-1 text-xs text-red-400">{errors.pid.message}</p>
            )}
          </div>

          {/* Username */}
          <div>
            <label htmlFor="edit-username" className="block text-sm font-medium text-foreground mb-1">
              Username (UID)
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">
              Respondent identifier from the uid URL parameter
            </p>
            <input
              id="edit-username"
              {...register('username', { required: 'Username is required' })}
              className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
              placeholder="e.g. resp_00421"
            />
            {errors.username && (
              <p className="mt-1 text-xs text-red-400">{errors.username.message}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label htmlFor="edit-status" className="block text-sm font-medium text-foreground mb-1">
              Survey Status
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">
              The outcome status returned by the survey platform
            </p>
            <select
              id="edit-status"
              {...register('status', { required: 'Status is required' })}
              className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
            >
              {statusOptions.map((opt) => (
                <option key={`status-opt-${opt.value}`} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.status && (
              <p className="mt-1 text-xs text-red-400">{errors.status.message}</p>
            )}
          </div>

          {/* IP */}
          <div>
            <label htmlFor="edit-ip" className="block text-sm font-medium text-foreground mb-1">
              IP Address
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">
              Detected respondent IP at time of survey completion
            </p>
            <input
              id="edit-ip"
              {...register('ip', {
                required: 'IP address is required',
                pattern: {
                  value: /^(\d{1,3}\.){3}\d{1,3}$/,
                  message: 'Enter a valid IPv4 address',
                },
              })}
              className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
              placeholder="e.g. 192.168.1.100"
            />
            {errors.ip && (
              <p className="mt-1 text-xs text-red-400">{errors.ip.message}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground border border-border hover:bg-white/5 transition-all duration-150 active:scale-95"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 text-primary-foreground border border-primary/50 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ minWidth: '110px', justifyContent: 'center' }}
            >
              {isSaving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save size={14} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}